import {
	Action,
	Dispatch,
} from 'redux';
// Import services
import {
	StringService,
} from '@app/services';
import {
	NotificationType,
	OverlayTarget,
} from '@app/enumerations';
// Import action type enumeration.
import ActionType from '@app/redux/actions/types';
// Import api models.
import {
	PossibleBusinessType,
	PossibleHeardThroughOption,
	PossibleMasterProducer,
} from '@app/models/api';
// Import controller factory.
import ControllerFactoryProvider from '@app/controllers/factory';
// Import action methods.
import {
	addNotification,
	endProcessing,
	startProcessing,
} from '@app/redux/actions/methods';

//#region Load Possible Heard Through Options Action

interface LoadPossibleHeardThroughOptionsParameters {
	possibleHeardThroughOptions: PossibleHeardThroughOption[];
}

type LoadPossibleHeardThroughOptionsAction = Action & LoadPossibleHeardThroughOptionsParameters;

const loadPossibleHeardThroughOptionsAction: (possibleHeardThroughOptions: PossibleHeardThroughOption[]) =>
	LoadPossibleHeardThroughOptionsAction = (possibleHeardThroughOptions: PossibleHeardThroughOption[]) => ({
		possibleHeardThroughOptions,
		type: ActionType.LOAD_POSSIBLE_HEARD_THROUGH_OPTIONS
	});

const loadPossibleHeardThroughOptions = () =>
	async (dispatch: Dispatch): Promise<PossibleHeardThroughOption[]> => {
		const messageId: string = `${StringService.getInstance().hashObject(undefined)}-loadPossibleHeardThroughOptions`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading heard about us options...',
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN,
			})(dispatch);

			let possibleHeardThroughOptions: PossibleHeardThroughOption[] = await ControllerFactoryProvider.getControllerFactory().getSignupController().getPossibleHeardThroughOptions();
			// Load possible states to Redux.
			dispatch<LoadPossibleHeardThroughOptionsAction>(loadPossibleHeardThroughOptionsAction(possibleHeardThroughOptions));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);

			return possibleHeardThroughOptions;
		} catch (error: any) {
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing
			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);
			// Throw error.
			throw error;
		}
	};

//#endregion Heard through actions

//#region Load Business Types Action.

interface LoadPossibleBusinessTypesParameters {
	possibleBusinessTypes: PossibleBusinessType[]
}

type LoadPossibleBusinessTypeAction = Action & LoadPossibleBusinessTypesParameters;

const loadPossibleBusinessTypeAction: (possibleBusinessTypes: PossibleBusinessType[]) =>
	LoadPossibleBusinessTypeAction = (possibleBusinessTypes: PossibleBusinessType[]) => ({
		possibleBusinessTypes,
		type: ActionType.LOAD_POSSIBLE_BUSINESS_TYPES
	});

const loadPossibleBusinessTypes = () =>
	async (dispatch: Dispatch): Promise<PossibleBusinessType[]> => {
		const messageId: string = `${StringService.getInstance().hashObject(undefined)}-loadPossibleBusinessTypes`;
		try {
			await startProcessing({
				message: 'Loading business types...',
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN,
			})(dispatch);

			let possibleBusinessTypes: PossibleBusinessType[] = await ControllerFactoryProvider.getControllerFactory().getSignupController().getPossibleBusinessTypes();
			// Load possible business types to Redux.
			dispatch<LoadPossibleBusinessTypeAction>(loadPossibleBusinessTypeAction(possibleBusinessTypes));

			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);

			return possibleBusinessTypes;
		} catch (error: any) {
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);
			// Throw error.
			throw error;
        }
	};

//#endregion load business types

//#region Load Possible Master Producers Action

interface LoadPossibleMasterProducersParameters {
	possibleMasterProducers: PossibleMasterProducer[];
}

type LoadPossibleMasterProducersAction = Action & LoadPossibleMasterProducersParameters;

const loadPossibleMasterProducersAction: (possibleMasterProducers: PossibleMasterProducer[]) =>
	LoadPossibleMasterProducersAction = (possibleMasterProducers: PossibleMasterProducer[]) => ({
		possibleMasterProducers,
		type: ActionType.LOAD_POSSIBLE_MASTER_PRODUCERS
	});

const loadPossibleMasterProducers = () =>
	async (dispatch: Dispatch): Promise<PossibleMasterProducer[]> => {
		const messageId: string = `${StringService.getInstance().hashObject(undefined)}-loadPossibleMasterProducers`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Loading producer agencies...',
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN,
			})(dispatch);

			// Load possible master producers.
			let possibleMasterProducers: PossibleMasterProducer[] = await ControllerFactoryProvider.getControllerFactory().getSignupController().getPossibleProducerAgencies();
			// Load possible master producers to Redux.
			dispatch<LoadPossibleMasterProducersAction>(loadPossibleMasterProducersAction(possibleMasterProducers));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);

			return possibleMasterProducers;
		} catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: error.message,
				secondaryMessage: error.systemMessage,
				notificationType: NotificationType.ERROR,
			})(dispatch);
			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: OverlayTarget.SCREEN
			})(dispatch);
			// Throw error.
			throw error;
		}
	};
//#endregion master producers



// Export actions.
export {
	LoadPossibleHeardThroughOptionsAction,
	LoadPossibleBusinessTypeAction,
	LoadPossibleMasterProducersAction,
};

// Export action methods.
export {
	loadPossibleHeardThroughOptions,
	loadPossibleBusinessTypes,
	loadPossibleMasterProducers,
};

// Export action parameters.
export {
	LoadPossibleHeardThroughOptionsParameters,
	LoadPossibleBusinessTypesParameters,
	LoadPossibleMasterProducersParameters,
};

// Export action factories.
export {
	loadPossibleHeardThroughOptionsAction,
	loadPossibleBusinessTypeAction,
	loadPossibleMasterProducersAction,
};